import { useCallback } from 'react';
import { Input } from '@heroui/react';
import { X } from 'lucide-react';

interface OverrideInputProps {
  currentValue: number | null;
  aiValue: number | null;
  unit: string;
  onChange: (value: number | null) => void;
  onClear: () => void;
}

export default function OverrideInput({
  currentValue,
  aiValue,
  unit,
  onChange,
  onClear,
}: OverrideInputProps) {
  const isOverridden = currentValue != null;

  const handleChange = useCallback(
    (val: string) => {
      if (val === '' || val === '-') {
        onChange(null);
        return;
      }
      const num = parseFloat(val);
      if (!isNaN(num) && num >= 0) {
        onChange(num);
      }
    },
    [onChange],
  );

  return (
    <div className="flex flex-col gap-1.5">
      {/* AI value */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          AI extracted
        </span>
        <span
          className="font-ui text-xs"
          style={{
            color: isOverridden ? 'var(--text-muted)' : 'var(--text-primary)',
            textDecoration: isOverridden ? 'line-through' : 'none',
          }}
        >
          {aiValue != null ? `${aiValue.toFixed(2)} ${unit}` : '--'}
        </span>
      </div>

      {/* Override input */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Override
        </span>
        <Input
          type="number"
          size="sm"
          placeholder="--"
          value={currentValue != null ? String(currentValue) : ''}
          onValueChange={handleChange}
          classNames={{
            base: 'max-w-[100px]',
            input: 'font-ui text-xs text-right',
            inputWrapper:
              'h-7 min-h-7 border-1 border-[var(--border)] bg-[var(--bg-base)] data-[hover=true]:border-[var(--border-em)] group-data-[focus=true]:border-[var(--accent)]',
          }}
          endContent={
            <span className="text-[10px] font-ui" style={{ color: 'var(--text-muted)' }}>
              {unit}
            </span>
          }
        />
        {isOverridden && (
          <button
            onClick={onClear}
            className="flex items-center justify-center w-5 h-5 rounded transition-colors hover:bg-[var(--bg-hover)]"
            style={{ color: 'var(--text-muted)' }}
            title="Clear override"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Show override value in accent if set */}
      {isOverridden && (
        <span className="font-ui text-xs font-semibold" style={{ color: 'var(--blue)' }}>
          {currentValue!.toFixed(2)} {unit}
        </span>
      )}
    </div>
  );
}
